import { Component, OnInit, Input, Output, EventEmitter, ViewChild, NgZone } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { StepCompletionExecution } from 'app/models/step-completion-execution';
import { StepCompletionExecutionService } from 'app/services/step-completion-execution.service';
import { WorkflowFile, WorkflowFileType } from 'app/models/workflow-file';
import { WorkflowForm } from 'app/models/workflow-form';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { FormSubmission } from 'app/models/form-submission';
import { FormTemplate } from 'app/models/template';
import { FormFillerComponent } from 'app/views/form-filler/form-filler.component';
import { AbstractPickedFile, StorageTypeEnum, CloudPickedFile, LocalPickedFile } from 'app/models/picked-file';
import { FilesFoldersPickerComponent } from 'app/shared/components/files-folders-picker/files-folders-picker.component';
import { AddEditWorkflowFormComponent } from '../../new-workflow-template/add-edit-workflow-form/add-edit-workflow-form.component';
import { WorkflowTemplateService } from 'app/services/workflow-template.service';
import { FormTemplateService } from 'app/services/form-template.service';
import { finalize } from 'rxjs/operators';
import { ToastService, ModalDirective } from 'ng-uikit-pro-standard';
import { AddEditWorkflowFileComponent } from '../../new-workflow-template/add-edit-workflow-file/add-edit-workflow-file.component';
import { Resumable, ResumableFile } from 'resumablejs';
import { WorkflowExecutionService } from 'app/services/workflow-execution.service';
import { WorkflowExecFile } from 'app/models/workflow-exec-file';
import { basename } from 'path';
import { ConnectedAppAuth } from 'app/models/connected-app';
import { ConnectedAppAuthorizeComponent } from 'app/views/connected-apps/connected-app-authorize/connected-app-authorize.component';
import { WorkflowStepCompletion } from 'app/models/workflow-step-completion';

@Component({
  selector: 'app-step-completion-execution',
  templateUrl: './step-completion-execution.component.html',
  styleUrls: ['./step-completion-execution.component.scss']
})
export class StepCompletionExecutionComponent implements OnInit {

  tableLimitSelect = [5, 10, 20, 30, 50, 100];
  selectedFormsTableLimit: number = 100;
  selectedFilesTableLimit: number = 100;

  private _stepCompletion: WorkflowStepCompletion;
  
  public stepCompletionExecutionFormGroup: FormGroup = null;
  
  filesList: {workflowFile: WorkflowFile, formGroup: FormGroup, resumable?: Resumable, uploadProgress?: number}[] = [];
  formsList: {workflowForm: WorkflowForm, formGroup: FormGroup}[] = [];

  pickFileIndex: number;
  fillFormIndex: number;

  @ViewChild('formFiller') formFiller: FormFillerComponent;
  @ViewChild('filePicker') filePicker: FilesFoldersPickerComponent;
  @ViewChild('addEditWorkflowForm') addEditWorkflowForm: AddEditWorkflowFormComponent;
  @ViewChild('addEditWorkflowFile') addEditWorkflowFile: AddEditWorkflowFileComponent;
  @ViewChild('passwordConfirmationModal') passwordConfirmationModal: ModalDirective;
  @ViewChild('externalConfirmationModal') externalConfirmationModal: ModalDirective;
  @ViewChild('authorizeApp') authorizeApp: ConnectedAppAuthorizeComponent;

  isLoadingForm: boolean = false;

  @Input() previousFormSubmissions: FormSubmission[] = null;
  @Input() previousFiles: WorkflowExecFile[] = null;

  @Input() backButtonLink: string = null;
  @Input() doneButtonText: string = 'Done';

  @Input() isExternal: boolean = false;
  @Input() workflowExecId: number = null;
  // required for initial step completion download attached file
  @Input() workflowTemplateId: number = null;
  // Required for external requests
  @Input() contactId: number = null;
  @Input() token: string = null;

  @Input() public set stepCompletion(s : WorkflowStepCompletion) {
    this._stepCompletion = s;
    this.stepCompletionExecutionFormGroup = this.stepCompletionExecutionService.getFormGroup(s, (this.stepCompletionExecutionFormGroup)? this.stepCompletionExecutionFormGroup.value: null);
    
    this.updateFilesAndFormsList();
  }

  private updateFilesAndFormsList() {
    this.filesList = [];
    this.formsList = [];
    this._stepCompletion.files.forEach((f: WorkflowFile) => {
      this.filesList.push({
        workflowFile: f,
        formGroup: this.stepCompletionExecutionFormGroup.get('files.' + f.name) as FormGroup
      });
    });
    this._stepCompletion.forms.forEach((f: WorkflowForm) => {
      this.formsList.push({
        workflowForm: f,
        formGroup: this.stepCompletionExecutionFormGroup.get('form_submissions.' + f.name) as FormGroup
      });
    });
  }

  public get stepCompletion() {
    return this._stepCompletion;
  }

  @Output() completed: EventEmitter<StepCompletionExecution> = new EventEmitter<StepCompletionExecution>();


  constructor(private workflowExecutionService: WorkflowExecutionService,
              public stepCompletionExecutionService: StepCompletionExecutionService,
              private workflowTemplateService: WorkflowTemplateService,
              private validationHandler: ValidationHandler,
              private formTemplateService: FormTemplateService,
              private toastService: ToastService,
              private ngZone: NgZone) { }

  ngOnInit(): void {
    this.fillFormIndex = -1;
    this.pickFileIndex = -1;
  }

  innerWidth() {
    return window.innerWidth;
  }
  
  authorizeUserAuths() {
    if (this.isExternal) {
      this.complete();
      return;
    }

    let requiredUserAuths: ConnectedAppAuth[] = this._stepCompletion.required_user_auths;
    if (requiredUserAuths && requiredUserAuths.length == 0) {
      this.complete();
    } else if (requiredUserAuths) {
      this.isLoadingForm = true;
      this.authorizeApp.showIfNeedsAuthorization(requiredUserAuths, false).pipe(finalize(() => this.isLoadingForm = false)).subscribe((isAllAuthorized) => {
        if (isAllAuthorized) {
          this.complete();
        }
      });
    } else {
      this.complete();
    }
  }

  // try to complete the stepCompletion
  // fires (completed) if all is valid and can be submitted
  complete() {
    this.validationHandler.onSubmitCheck(this.stepCompletionExecutionFormGroup);
    if (!this.stepCompletionExecutionFormGroup.valid) {
      window.scroll(0,0);
      return;
    }

    if (this._stepCompletion.should_confirm) {
      if (this.isExternal) {
        this.externalConfirmationModal.show();
      } else {
        // ask for password confirmation
        this.passwordConfirmationModal.show();
      }
    } else {
      this.completed.emit(this.stepCompletionExecutionFormGroup.value);
    }
  }

  onExternalConfirmation() {
    this.externalConfirmationModal.hide();
    this.completed.emit(this.stepCompletionExecutionFormGroup.value);
  }

  onPasswordConfirmation() {
    this.passwordConfirmationModal.hide();
    this.completed.emit(this.stepCompletionExecutionFormGroup.value);
  }

  fillForm(index: number) {
    this.fillFormIndex = index;
    const fillFormCallback = () => {
      let previousFormSubmission = (this.previousFormSubmissions || []).find((f) => f.name == this.formsList[index].workflowForm.name);
      this.formFiller.show(this.formsList[index].workflowForm.form_template, {
                                                                              name: this.formsList[index].workflowForm.name,
                                                                              template_id:this.formsList[index].workflowForm.form_template_id,
                                                                              value: (this.formsList[index].formGroup.value.value || this.formsList[index].workflowForm.is_existing == false)? 
                                                                                      this.formsList[index].formGroup.value.value: 
                                                                                      previousFormSubmission.value,
                                                                              timezone: (this.formsList[index].formGroup.value.value || this.formsList[index].workflowForm.is_existing == false)?
                                                                                      Intl.DateTimeFormat().resolvedOptions().timeZone:
                                                                                      (previousFormSubmission && previousFormSubmission.timezone)
                                                                            },
                                                                            this.formsList[index].workflowForm.name,
                                                                            this.formsList[index].workflowForm.required_fields,
                                                                            true, false, false, this.formsList[index].workflowForm.optional_fields);
    };
    if (this.formsList[index].workflowForm.form_template) {
      fillFormCallback();
    } else {
      this.isLoadingForm = true;
      this.formTemplateService.getById(this.formsList[index].workflowForm.form_template_id).pipe(finalize(() => {
        this.isLoadingForm = false;
      })).subscribe((f) => {
        this.formsList[index].workflowForm.form_template = f;
        fillFormCallback();
      }, () => {
        this.toastService.error('Error loading form!');
      });
    }
  }

  onFormFilled(event: {form: FormTemplate, submission: FormSubmission}) {

    if (this.formsList[this.fillFormIndex].workflowForm.is_existing) {
      this.formsList[this.fillFormIndex].formGroup.get('id').setValue(this.previousFormSubmissions.find((f) => f.name == this.formsList[this.fillFormIndex].workflowForm.name).id);
    }

    this.formsList[this.fillFormIndex].formGroup.get('template_id').setValue(event.form.id);
    this.formsList[this.fillFormIndex].formGroup.get('value').setValue(event.submission.value);

    this.fillFormIndex = -1;
  }

  downloadFile(index: number) {
    if (this.filesList[index].workflowFile.file_type_id == WorkflowFileType.FILE_TYPE_EXISTING) {
      if (this.isExternal) {
        this.workflowExecutionService.downloadFileExternal(this.workflowExecId, this.previousFiles.find((f) => /(.+?)(\.[^.]*$|$)/.exec(f.file_name)[1] == this.filesList[index].workflowFile.name).file_name, this.contactId, this.token);
      } else {
        this.workflowExecutionService.downloadFile(this.workflowExecId, this.previousFiles.find((f) => /(.+?)(\.[^.]*$|$)/.exec(f.file_name)[1] == this.filesList[index].workflowFile.name).file_name);
      }
    } else if (this.filesList[index].workflowFile.file_type_id == WorkflowFileType.FILE_TYPE_ATTACHED) {
      if (this.isExternal) {
        this.workflowExecutionService.downloadAttachedFileExternal(this.workflowExecId, this.filesList[index].workflowFile.name, this.stepCompletionExecutionFormGroup.value.name, this.contactId, this.token);
      } else {
        if (this.workflowExecId) {
          this.workflowExecutionService.downloadAttachedFile(this.workflowExecId, this.filesList[index].workflowFile.name, this.stepCompletionExecutionFormGroup.value.name);
        } else if (this.workflowTemplateId) {
          this.workflowExecutionService.downloadInitialAttachedFile(this.workflowTemplateId, this.filesList[index].workflowFile.name);
        }
      }
    }
  }

  openFilePicker(index: number) {
    this.pickFileIndex = index;
    if (this.filesList[index].workflowFile.file_type_id == WorkflowFileType.FILE_TYPE_EXISTING) {
      try {
        this.filePicker.fileExtension =[/(.+?)(\.[^.]*$|$)/.exec(this.previousFiles.find((f) => /(.+?)(\.[^.]*$|$)/.exec(f.file_name)[1] == this.filesList[index].workflowFile.name).file_name)[2].substring(1)];
      } catch (e) {
        this.filePicker.fileExtension = [];
      }
    } else {
      this.filePicker.fileExtension = [];
    }
    this.filePicker.show();
  }
  
  onPickedFile(e: AbstractPickedFile[]) {
    if (e[0].storageType == StorageTypeEnum.LOCAL) {
      /** @todo handle file uploads and temporary locations */
      const pickedLocalFile = e[0] as LocalPickedFile;

      if (this.isExternal) {
        this.filesList[this.pickFileIndex].resumable = this.workflowExecutionService.getNewUploadTempFileResumableExternal(this.workflowExecId, this.contactId, this.token);
      } else {
        this.filesList[this.pickFileIndex].resumable = this.workflowExecutionService.getNewUploadTempFileResumable();
      }
      this.filesList[this.pickFileIndex].uploadProgress = 0;
      this.filesList[this.pickFileIndex].resumable.addFile(pickedLocalFile.file, undefined);
      this.filesList[this.pickFileIndex].resumable.on('fileSuccess', (file, message) => {
        message = JSON.parse(message);
        this.filesList[this.pickFileIndex].formGroup.get('storage_id').setValue(message.storage_id);
        this.filesList[this.pickFileIndex].formGroup.get('path').setValue(message.file_path);
        this.filesList[this.pickFileIndex].resumable = null;
        this.pickFileIndex = -1;
        this.ngZone.run(() => this.filesList = [...this.filesList]);
      });
      this.filesList[this.pickFileIndex].resumable.on('error', () => {
        this.toastService.error('Error uploading file!');
        this.filesList[this.pickFileIndex].resumable = null;
        this.pickFileIndex = -1;
        this.ngZone.run(() => this.filesList = [...this.filesList]);
      });

      this.filesList[this.pickFileIndex].resumable.on('fileAdded', (f: ResumableFile) => {
        f.resumableObj.upload();
      });
      this.filesList[this.pickFileIndex].resumable.on('fileProgress', (f: ResumableFile) => {
        this.filesList[this.pickFileIndex].uploadProgress = f.progress(false) * 100;
        this.ngZone.run(() => this.filesList = [...this.filesList]);
      });
    } else {
      const pickedCloudFile = e[0] as CloudPickedFile;
      this.filesList[this.pickFileIndex].formGroup.get('storage_id').setValue(pickedCloudFile.storageId);
      this.filesList[this.pickFileIndex].formGroup.get('path').setValue(pickedCloudFile.path);

      this.pickFileIndex = -1;
    }    
  }

  removeFile(index: number) {
    this._stepCompletion.files.splice(index, 1);
    this.stepCompletion = this._stepCompletion;  
  }

  removeForm(index: number) {
    this._stepCompletion.forms.splice(index, 1);
    this.stepCompletion = this._stepCompletion;  
  }

  onAddForm() {
    let frmGroup: FormGroup = this.workflowTemplateService.getWorkflowFormFormGroup({isAdditional: true} as WorkflowForm, null, true);
    this.addEditWorkflowForm.show(frmGroup, false, true);
  }

  onAddFormFinished(frm: FormGroup) {
    this.isLoadingForm = true;
    this.formTemplateService.getById(frm.value.form_template_id).pipe(finalize(() => {
      this.isLoadingForm = false;
    })).subscribe((f) => {
      frm.get('form_template').setValue(f);
      let formsArray: WorkflowForm[] = (this._stepCompletion.forms || []);
      formsArray.push(frm.value);
  
      this.stepCompletion = this._stepCompletion;  

      this.fillForm(formsArray.length - 1);
    }, () => {
      this.toastService.error('Error loading form!');
    });
  }

  onAddFile() {
    let frmGroup: FormGroup = this.workflowTemplateService.getWorkflowFileFormGroup({isAdditional: true} as WorkflowFile, null, true);
    this.addEditWorkflowFile.show(frmGroup);
  }

  onAddFileFinished(frm: FormGroup) {
    let filesArray: WorkflowFile[] = (this._stepCompletion.files || []);
    filesArray.push(frm.value);

    this.stepCompletion = this._stepCompletion;  
  }
}
